/**
 * Portfolio Admin - Gestión de secciones e imágenes del portfolio
 *
 * Permite a los administradores:
 * - Crear/editar/eliminar secciones del portfolio
 * - Agregar/eliminar imágenes de cada sección
 * - Reordenar imágenes
 * - Cambiar títulos de secciones
 *
 * Auto-guarda al subir imágenes; el botón manual cubre reordenamiento y títulos.
 */

import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminSaveBar } from "@/components/admin/AdminSaveBar";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges";
import { apiErrorMessage, readJsonResponse } from "@/lib/readJsonResponse";
import { ImageUploader } from "@/components/ImageUploader";
import {
  invalidatePortfolioSectionsCache,
  parsePortfolioUrl,
  serializePortfolioUrl,
} from "@/config/portfolioConfig";
import { resolveLogoUrl, resolvePortfolioImageUrl } from "@/lib/cloudinaryUtils";
import { BRAND_CLOUDINARY_FOLDER } from "@/lib/brandConfig";
import {
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  Info,
  Loader2,
  Plus,
  Trash2,
  GripVertical,
  XCircle,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

// Config de Cloudinary visible desde el frontend (baked at build time)
const CLOUD_NAME_CFG = (import.meta.env.VITE_CLOUDINARY_CLOUD_NAME as string)?.trim() || "";
const PRESET_CFG =
  (import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET as string)?.trim() ||
  (BRAND_CLOUDINARY_FOLDER ? `${BRAND_CLOUDINARY_FOLDER}-unsigned` : "");
const CLOUDINARY_READY = !!(CLOUD_NAME_CFG && PRESET_CFG && BRAND_CLOUDINARY_FOLDER);

interface PortfolioSection {
  id: string;
  title: string;
  urls: string[];
  order: number;
}

function snapshotOf(sections: PortfolioSection[], portfolioPublicEnabled: boolean): string {
  return JSON.stringify({ sections, portfolioPublicEnabled });
}

export default function PortfolioAdmin() {
  const { toast } = useToast();
  const { getAuthHeaders, isLoaded } = useAdminAuth();
  const [sections, setSections] = useState<PortfolioSection[]>([]);
  const [portfolioPublicEnabled, setPortfolioPublicEnabled] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [savedSnapshot, setSavedSnapshot] = useState<string | null>(null);

  const currentSnapshot = useMemo(
    () => snapshotOf(sections, portfolioPublicEnabled),
    [sections, portfolioPublicEnabled],
  );
  const isDirty = savedSnapshot !== null && currentSnapshot !== savedSnapshot;
  useUnsavedChanges(isDirty);

  useEffect(() => {
    if (!isLoaded) return;
    loadSections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded]);

  // Cmd/Ctrl+S para guardar
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.key !== "s") return;
      e.preventDefault();
      if (isLoading || isSaving) return;
      if (isDirty) void saveSections();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isSaving, isDirty]);

  const loadSections = async () => {
    try {
      setIsLoading(true);
      const headers = await getAuthHeaders();
      const response = await fetch("/api/admin/portfolio", { headers });

      const body = await readJsonResponse(response);
      if (!response.ok) {
        const msg = apiErrorMessage(body, "Failed to load portfolio");
        if (response.status === 503) {
          toast({ title: "Base de datos no lista", description: msg, variant: "destructive" });
        }
        throw new Error(msg);
      }

      const data = body as {
        portfolioPublicEnabled?: boolean;
        sections?: { id?: string; title?: string; urls?: string[]; order?: number }[];
      };
      const nextPub = data.portfolioPublicEnabled !== false;
      const nextSections = (data.sections || []).map(
        (s: { id?: string; title?: string; urls?: string[]; order?: number }) => ({
          id: s.id ?? `section-${Date.now()}-${Math.random().toString(36).slice(2)}`,
          title: s.title ?? "Nueva Sección",
          urls: Array.isArray(s.urls) ? s.urls : [],
          order: typeof s.order === "number" ? s.order : 0,
        }),
      );
      setPortfolioPublicEnabled(nextPub);
      setSections(nextSections);
      setSavedSnapshot(snapshotOf(nextSections, nextPub));
    } catch (error) {
      console.error("Error loading portfolio:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "No se pudieron cargar las secciones del portfolio",
        variant: "destructive",
      });
      setSections([]);
    } finally {
      setIsLoading(false);
    }
  };

  const isSavingRef = useRef(false);
  const pendingSaveRef = useRef<{ sections: PortfolioSection[]; pub: boolean } | null>(null);

  /** Persiste las secciones pasadas directamente (evita closure stale de React state).
   *  Devuelve true si el guardado completó con éxito, false si fue encolado o falló.
   *  Si llega otra solicitud mientras hay un guardado en curso, la encola y la ejecuta al terminar. */
  const persistSections = useCallback(
    async (nextSections: PortfolioSection[], nextPub: boolean, silent = false): Promise<boolean> => {
      if (isSavingRef.current) {
        pendingSaveRef.current = { sections: nextSections, pub: nextPub };
        return false;
      }
      isSavingRef.current = true;
      setIsSaving(true);
      let success = false;
      try {
        const headers = await getAuthHeaders();
        const response = await fetch("/api/admin/portfolio", {
          method: "POST",
          headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify({ sections: nextSections, portfolioPublicEnabled: nextPub }),
        });
        const saveBody = await readJsonResponse(response);
        if (!response.ok) throw new Error(apiErrorMessage(saveBody, "Failed to save portfolio"));

        invalidatePortfolioSectionsCache();
        try { localStorage.setItem("portfolio_updated", String(Date.now())); } catch { /* no-op */ }
        setSavedSnapshot(snapshotOf(nextSections, nextPub));
        if (!silent) {
          toast({ title: "Guardado", description: "Los cambios ya están en el sitio público." });
        }
        success = true;
      } catch (error) {
        console.error("Error saving portfolio:", error);
        toast({
          title: "Error",
          description: error instanceof Error ? error.message : "No se pudo guardar el portfolio",
          variant: "destructive",
        });
      } finally {
        isSavingRef.current = false;
        setIsSaving(false);
      }
      const pending = pendingSaveRef.current;
      if (pending) {
        pendingSaveRef.current = null;
        void persistSections(pending.sections, pending.pub, silent);
      }
      return success;
    },
    [getAuthHeaders, toast],
  );

  const saveSections = () => persistSections(sections, portfolioPublicEnabled);

  /** Auto-guarda y publica cuando se sube una imagen nueva; muestra el toast solo al confirmar el guardado */
  const autoSaveWithSections = async (newSections: PortfolioSection[]) => {
    setSections(newSections);
    const saved = await persistSections(newSections, portfolioPublicEnabled, true);
    if (saved) {
      toast({ title: "✓ Imagen publicada", description: "Ya aparece en el sitio." });
    }
  };

  const addSection = () => {
    const newSection: PortfolioSection = {
      id: `section-${Date.now()}`,
      title: `Nueva Sección ${sections.length + 1}`,
      urls: [],
      order: sections.length,
    };
    setSections([...sections, newSection]);
  };

  const removeSection = (id: string) => {
    setSections(sections.filter(s => s.id !== id));
  };

  const updateSectionTitle = (id: string, title: string) => {
    setSections(sections.map(s => s.id === id ? { ...s, title } : s));
  };

  const removeImageUrl = (sectionId: string, urlIndex: number) => {
    setSections(sections.map(s =>
      s.id === sectionId
        ? { ...s, urls: s.urls.filter((_, i) => i !== urlIndex) }
        : s
    ));
  };

  const toggleWide = (sectionId: string, urlIndex: number) => {
    setSections((prev) =>
      prev.map((s) => {
        if (s.id !== sectionId || !s.urls[urlIndex]) return s;
        const entry = parsePortfolioUrl(s.urls[urlIndex]);
        const newUrl = serializePortfolioUrl(entry.url, !entry.wide);
        const newUrls = [...s.urls];
        newUrls[urlIndex] = newUrl;
        return { ...s, urls: newUrls };
      })
    );
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const imageCount = sections.reduce((acc, s) => acc + s.urls.length, 0);

  return (
    <div className="mx-auto max-w-6xl space-y-8">
      <AdminPageHeader
        description={
          <>
            Secciones e imágenes Cloudinary en home y <code className="text-xs">/portfolio</code>.
            Métricas de vistas → Métricas fotos.
          </>
        }
        actions={
          <>
            {sections.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="secondary">
                  {sections.length} {sections.length === 1 ? "sección" : "secciones"}
                </Badge>
                <Badge variant="outline">{imageCount} imágenes</Badge>
              </div>
            )}
            {isDirty && (
              <span className="rounded-full border border-amber-500/40 bg-amber-950/40 px-2 py-0.5 text-xs font-medium text-amber-100">
                Sin guardar
              </span>
            )}
            <Button onClick={addSection} variant="outline">
              <Plus className="mr-2 h-4 w-4" />
              Agregar sección
            </Button>
          </>
        }
      />

      {/* Diagnóstico de configuración Cloudinary */}
      {CLOUDINARY_READY ? (
        <Alert className="border-green-500/30 bg-green-500/5">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertTitle className="text-green-700">Subida automática</AlertTitle>
          <AlertDescription className="text-green-700/80 space-y-0.5 text-xs">
            <p>
              Las imágenes se publican <strong>automáticamente</strong> al subirlas — no hace falta
              guardar manualmente. Las imágenes verticales aparecen en la galería de la home y en{" "}
              /portfolio. Las horizontales solo en /portfolio.
            </p>
            <p>
              Cloud:{" "}
              <code className="rounded bg-green-500/10 px-1">{CLOUD_NAME_CFG}</code> · Carpeta:{" "}
              <code className="rounded bg-green-500/10 px-1">{BRAND_CLOUDINARY_FOLDER}</code> · Preset:{" "}
              <code className="rounded bg-green-500/10 px-1">{PRESET_CFG}</code>
            </p>
          </AlertDescription>
        </Alert>
      ) : (
        <Alert variant="destructive" className="border-amber-500/40 bg-amber-950/20 text-amber-200">
          <AlertTriangle className="h-4 w-4 text-amber-400" />
          <AlertTitle className="text-amber-300">Cloudinary no configurado — subida deshabilitada</AlertTitle>
          <AlertDescription className="space-y-2 text-amber-200/80 text-xs">
            <p>
              Configurá estos env vars en <strong>Railway → Variables</strong> (servicio web):
            </p>
            <div className="rounded border border-amber-500/30 bg-black/30 p-2 font-mono space-y-1">
              {!CLOUD_NAME_CFG && (
                <div className="flex items-center gap-2">
                  <XCircle className="w-3 h-3 text-red-400 shrink-0" />
                  <span>
                    <strong>VITE_CLOUDINARY_CLOUD_NAME</strong> = tu cloud name
                  </span>
                </div>
              )}
              {CLOUD_NAME_CFG && !PRESET_CFG && (
                <div className="flex items-center gap-2">
                  <XCircle className="w-3 h-3 text-red-400 shrink-0" />
                  <span>
                    <strong>VITE_CLOUDINARY_UPLOAD_PRESET</strong> = tu preset unsigned
                  </span>
                </div>
              )}
              <div className="flex items-center gap-2 text-amber-400/60">
                <Info className="w-3 h-3 shrink-0" />
                <span>
                  <strong>CLOUDINARY_API_KEY</strong> + <strong>CLOUDINARY_API_SECRET</strong> → upload por servidor (fallback)
                </span>
              </div>
            </div>
            <p>
              Mientras tanto, podés pegar URLs o public_ids de Cloudinary directamente en cada sección.
            </p>
          </AlertDescription>
        </Alert>
      )}

      <Card className="border-border/80 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Portfolio público</CardTitle>
          <CardDescription>
            Si está desactivado, /portfolio y la galería de la home no muestran estas imágenes. Podés seguir editando aquí.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between gap-4 rounded-lg border border-border/60 bg-muted/30 px-4 py-3">
            <Label htmlFor="portfolio-public-enabled" className="cursor-pointer font-medium leading-snug">
              Mostrar portfolio en el sitio público
            </Label>
            <Switch
              id="portfolio-public-enabled"
              checked={portfolioPublicEnabled}
              onCheckedChange={setPortfolioPublicEnabled}
            />
          </div>
        </CardContent>
      </Card>

      {sections.length === 0 && (
        <Card className="border-dashed border-primary/30 bg-muted/30">
          <CardContent className="space-y-2 pt-6 text-center">
            <p className="text-sm font-medium">Creá tu portfolio</p>
            <p className="text-xs text-muted-foreground">
              Podés agregar todas las secciones que necesites (ej. Lookbook, Editorial) y tantas imágenes Cloudinary
              por sección como quieras.
            </p>
            <p className="text-xs text-muted-foreground">
              URL completa o <code className="rounded bg-muted px-1 text-[10px]">public_id</code> (ej.{" "}
              <code className="rounded bg-muted px-1 text-[10px]">{BRAND_CLOUDINARY_FOLDER}/…</code>).
            </p>
          </CardContent>
        </Card>
      )}

      {sections.length > 0 && (
        <div className="space-y-6">
          {sections.map((section, sectionIndex) => (
            <Card key={section.id} className="border-border/80 shadow-sm">
              <CardHeader>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center gap-2">
                      <GripVertical className="w-5 h-5 text-muted-foreground" />
                      <CardTitle className="text-xl">Sección {sectionIndex + 1}</CardTitle>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`title-${section.id}`}>Título</Label>
                      <Input
                        id={`title-${section.id}`}
                        value={section.title}
                        onChange={(e) => updateSectionTitle(section.id, e.target.value)}
                        placeholder="Ej: Editorial Verano 2024"
                        className="max-w-md"
                      />
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeSection(section.id)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Imágenes ({section.urls.length})</Label>
                  <CardDescription>
                    Solo imágenes Cloudinary (se aplica watermark). Carpeta:{" "}
                    <code className="text-xs bg-muted px-1 rounded">{BRAND_CLOUDINARY_FOLDER}/&lt;título-sección&gt;</code>
                    . Para fotos horizontales, marcá &quot;Horizontal&quot; y ocuparán 2 columnas en el grid.
                  </CardDescription>
                </div>

                {/* Lista de imágenes */}
                {section.urls.filter((u) => u?.trim()).length > 0 && (
                  <div className="space-y-2">
                    {section.urls
                      .map((raw, urlIndex) => ({ entry: parsePortfolioUrl(raw?.trim() ?? ""), urlIndex }))
                      .filter(({ entry }) => entry.url)
                      .map(({ entry, urlIndex }) => (
                      <div key={urlIndex} className="flex items-center gap-2">
                        <div className="flex-1 flex items-center gap-2 p-2 bg-muted rounded-md">
                          <img
                            src={resolvePortfolioImageUrl(entry.url, section.title) ?? resolveLogoUrl(entry.url) ?? entry.url}
                            alt={`Preview ${urlIndex + 1}`}
                            className={`object-cover rounded ${entry.wide ? "w-24 h-12" : "w-12 h-12"}`}
                            onError={(e) => {
                              const img = e.target as HTMLImageElement;
                              img.onerror = null;
                              img.style.display = "none";
                            }}
                          />
                          <span className="text-sm text-muted-foreground flex-1 truncate">
                            {entry.url}
                            {entry.wide && (
                              <span className="ml-1 text-xs text-blue-600">(horizontal)</span>
                            )}
                          </span>
                        </div>
                        <Button
                          variant={entry.wide ? "secondary" : "outline"}
                          size="sm"
                          onClick={() => toggleWide(section.id, urlIndex)}
                          title={entry.wide ? "Quitar formato horizontal" : "Marcar como horizontal (2 columnas)"}
                          className="shrink-0 text-xs"
                        >
                          {entry.wide ? "↔ Horizontal" : "Horizontal"}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeImageUrl(section.id, urlIndex)}
                          className="text-destructive hover:text-destructive shrink-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Agregar nueva imagen — auto-guarda al subir */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Label>Agregar Imágenes</Label>
                    {isSaving && (
                      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                        <Loader2 className="w-3 h-3 animate-spin" /> Publicando…
                      </span>
                    )}
                    {!isSaving && !isDirty && savedSnapshot !== null && (
                      <span className="inline-flex items-center gap-1 text-xs text-green-600">
                        <CheckCircle2 className="w-3 h-3" /> Publicado
                      </span>
                    )}
                  </div>
                  <ImageUploader
                    preferCloudinary
                    cloudinaryFolder={section.title}
                    onImageAdded={(url) => {
                      if (!url.trim()) return;
                      const newSections = sections.map((s) =>
                        s.id === section.id ? { ...s, urls: [...s.urls, url.trim()] } : s
                      );
                      autoSaveWithSections(newSections);
                    }}
                    onBulkImagesAdded={(urls) => {
                      const validUrls = urls.filter((u) => u?.trim());
                      if (validUrls.length === 0) return;
                      const newSections = sections.map((s) =>
                        s.id === section.id
                          ? { ...s, urls: [...s.urls, ...validUrls] }
                          : s
                      );
                      autoSaveWithSections(newSections);
                    }}
                  />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Preview link */}
      <Card className="border-border/80 bg-muted/30 shadow-sm">
        <CardContent className="flex flex-col gap-4 pt-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-medium">Vista previa</p>
            <p className="text-xs text-muted-foreground">Abrí el portfolio público en otra pestaña.</p>
          </div>
          <Link to="/portfolio" target="_blank" rel="noopener noreferrer">
            <Button variant="secondary">
              <ExternalLink className="mr-2 h-4 w-4" />
              Ver portfolio público
            </Button>
          </Link>
        </CardContent>
      </Card>

      <AdminSaveBar
        isDirty={isDirty}
        isSaving={isSaving}
        onSave={() => void saveSections()}
        onDiscard={() => void loadSections()}
      />
    </div>
  );
}
